import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";

import { isRTLLanguage } from "@/i18n/languageConfig";
import { createLTRTheme, createRTLTheme } from "@/theme/theme";

// Create rtl cache
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create ltr cache
const cacheLtr = createCache({
  key: "muiltr",
});

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();

  const activeLanguage = i18n.resolvedLanguage || i18n.language;
  const isRtl = useMemo(() => isRTLLanguage(activeLanguage), [activeLanguage]);
  const theme = useMemo(() => (isRtl ? createRTLTheme() : createLTRTheme()), [isRtl]);
  const cache = useMemo(() => (isRtl ? cacheRtl : cacheLtr), [isRtl]);

  useEffect(() => {
    const direction = isRtl ? "rtl" : "ltr";
    const root = document.getElementById("root");

    document.documentElement.dir = direction;
    document.documentElement.lang = activeLanguage || "en";
    document.body.dir = direction;
    document.body.setAttribute("data-direction", direction);
    root?.setAttribute("dir", direction);
    root?.setAttribute("lang", activeLanguage || "en");
  }, [activeLanguage, isRtl]);

  return (
    <CacheProvider value={cache}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </CacheProvider>
  );
};
